import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { PostHogProvider } from 'posthog-js/react';
import type { PostHogConfig } from 'posthog-js';
import App from './App';
import { initPushServiceWorker } from './lib/push';
import { initKioskStability, isKioskModeEnabled } from './lib/kiosk-stability';
import { initKioskErrorReporter } from './lib/kiosk-error-reporter';
import { initKioskHeartbeat } from './lib/kiosk-heartbeat';
import { ToastProvider } from './context/ToastContext';
import { DateProvider } from './context/DateContext';
import './index.css';

// Register service worker for push notifications
initPushServiceWorker();

// Initialize kiosk stability features (auto-reload, fetch timeouts, failure tracking)
initKioskStability();

// Register global error handlers for kiosk crash detection
initKioskErrorReporter();

// Liveness heartbeat: detects real main-thread/renderer freezes (vs screen-off) in kiosk mode
initKioskHeartbeat();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// In kiosk mode (24/7 wall-tablet session) PostHog's default autocapture,
// session recording, and event queues grow unboundedly and contribute to OOM
// restarts on the 256 MB Android WebView heap.  Disable the memory-hungry
// features while keeping basic event tracking available for non-kiosk users.
const _kioskMode = isKioskModeEnabled();
const posthogOptions: Partial<PostHogConfig> = _kioskMode
  ? {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      defaults: '2025-11-30',
      capture_pageview: false,
      capture_pageleave: false,
      autocapture: false,
      disable_session_recording: true,
    }
  : {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      defaults: '2025-11-30',
      capture_pageview: true,
      capture_pageleave: true,
    };

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={posthogOptions}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <DateProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </DateProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </PostHogProvider>
  </React.StrictMode>
);
